import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { PageHelpButton } from '@/src/renderer/components/common/PageHelpButton'
import { useScrollbarVisibility } from '@/src/renderer/hooks/useScrollbarVisibility'
import { useTranslation } from 'react-i18next'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { SortableContext } from '@dnd-kit/sortable'
import { UpcomingOpenIcon } from '../../../../components/icons/UpcomingIcon'
import { useDataSource } from '../../../../lib/dataSource'
import type { Task } from '../../../../lib/db/models'
import { TaskEntry, TitleRecognitionScope, type TaskCreationData, type TaskEntryData } from '../../components/TaskEntry'
import { useMidnightRefresh } from '../../hooks/useMidnightRefresh'
import { useTaskOperations } from '../../hooks/useTaskOperations'
import { useUpcomingSelection, EMPTY_SELECTION } from './hooks/useUpcomingSelection'
import { duplicateTask } from '../../utils/duplicateTask'
import { toCreateTask } from '../../utils/toCreateTask'
import { orderAtEndOf, orderAtEndOfOwnPlace } from '../../utils/appendTaskOrders'
import { showCreatedElsewhereToast } from '../../utils/createdElsewhereToast'
import { MonthSelector } from './components/MonthSelector'
import { MonthTitle } from './components/MonthTitle'
import { TaskDetails, DetailsToggle } from '../../components/TaskDetails'
import { useTaskDetailsPane } from '../../hooks/useTaskDetailsPane'
import { TaskDetailsOpenerProvider } from '../../hooks/TaskDetailsOpener'
import { SelectionHullScope } from '../../components/SelectionHull'
import { topVisibleTaskId } from '../../hooks/useTopVisibleTask'
import { MonthBlock } from './components/MonthBlock'
import { useCompletionGrace } from '../../hooks/useCompletionGrace'
import { useContextMenuBadgeUpdate } from '../../hooks/useContextMenuBadgeUpdate'
import { useContextMenuMarks } from '../../hooks/useContextMenuMarks'
import { useAppTaskMenu, menuAnchorOf, useShowTaskInProject } from '../../hooks/useAppTaskMenu'
import { MoveMenuContext } from '../../utils/moveMenu'
import { useMoveMenu } from '../../hooks/useMoveMenu'
import { useTaskMenuShortcuts } from '../../hooks/useTaskMenuShortcuts'
import { useFavoriteShortcut } from '../../hooks/useFavoriteShortcut'
import { routeDetailsUpdate } from '../../utils/detailsUpdate'
import { consumePendingPageJump, putBackPageJump } from '@/src/renderer/lib/taskJump/pendingTaskJump'
import { whenTaskRowSettled } from '@/src/renderer/lib/taskJump/whenTaskRowSettled'
import { scrollRowAndPin } from './hooks/scrollRowAndPin'
import { onAppEvent } from '@/src/renderer/lib/events'
import { wakeTaskUpdate } from '../../utils/buildTaskUpdate'
import type { TaskMenuTarget } from '../../components/TaskMenu/TaskMenu.types'
import { useUpcomingTasks } from './hooks/useUpcomingTasks'
import { useVisibleMonth } from './hooks/useVisibleMonth'
import { useVisibleMonthRange } from './hooks/useVisibleMonthRange'
import { useScrollAnchor } from './hooks/useScrollAnchor'
import {
  addMonths,
  dateKey,
  dropIndexInDay,
  monthKey,
  planDayReorder,
  startOfDay,
  startOfMonth,
  type DayBucket,
} from './hooks/upcomingTasks.utils'
import { useDayAwareStrategy } from './hooks/useDayAwareStrategy'
import type { UpcomingPageProps } from './UpcomingPage.types'
import { useIsActiveTab, useTabId } from '../../../../pages/TabStack/ActiveTabContext'
import { useCanvasFocus } from '../../../../contexts/CanvasFocusContext'
import '../TaskViews.scss'
// Die Klassen für das Nebeneinander von Liste und Aufgaben-Details liegen dort.
import '../ProjectPage/ProjectPage.scss'
import './UpcomingPage.scss'
import { TaskRowDataScope } from '../../components/TaskRowData'
import { upcomingDropTarget } from './hooks/upcomingDropTarget'
import { LazyRowScope } from '../../components/LazyRows'
import { usePlayTask } from '@/src/renderer/lib/focus'

// Pixel distance from top/bottom of scroll container at which we extend the
// rendered month range. Larger = earlier load; smaller = closer to viewport.
const LAZY_LOAD_THRESHOLD_PX = 400


// Height of the top/bottom fade overlays on the scroll area (mirrors
// `.upcoming-page__fade-*` height in SCSS). Keyboard selections must land
// outside this band, plus a visual breathing margin, so the selected row
// isn't hidden under the blur gradient — generous enough that the row looks
// clearly above the fade, not just technically outside it.
const FADE_HEIGHT_PX = 48
const KEYBOARD_SCROLL_MARGIN_PX = 12

// Extra vertical nudge below the title offset so the first day row isn't
// crammed right up against the fade edge after a chip jump / initial scroll.
const SCROLL_JUMP_PADDING_PX = -16

/**
 * Scroll `scrollEl` so that the month-block under `anchorEl` starts at the
 * top of the viewport WITH its inline title already pushed above the top
 * edge — the first day row then sits directly below the fade overlay.
 *
 * Without the title offset, the inline MonthTitle would linger inside the
 * fade band, showing "April" twice (pinned + inline-under-blur).
 */
function scrollMonthIntoPosition(scrollEl: HTMLElement, anchorEl: HTMLElement): void {
  const rootRect = scrollEl.getBoundingClientRect()
  const anchorRect = anchorEl.getBoundingClientRect()
  scrollEl.scrollTop += (anchorRect.top - rootRect.top) - monthJumpOffset(anchorEl)
}

/** Wo die Oberkante des Monatsblocks nach einem Sprung hingehört (negativ: Titel über dem Rand).
 *  Der Anker braucht das ZIEL, nicht die erreichte Stelle — die kann noch geklemmt sein. */
function monthJumpOffset(anchorEl: HTMLElement): number {
  const titleEl = anchorEl.querySelector<HTMLElement>('.month-block__title')
  return -((titleEl?.offsetHeight ?? 0) + SCROLL_JUMP_PADDING_PX)
}

/**
 * Was die **Zeitleiste** neben offenen Aufgaben-Details mindestens braucht (#226): Tageszahl,
 * Wochentag und um 36 px eingerückte Aufgaben (`DayGroup.scss`). Bei ~530 px fielen die Titel weg;
 * eine Aufgabenliste kommt mit 360 px aus (`LIST_MIN_WIDTH`). Darunter weicht die Navigation.
 */
const UPCOMING_LIST_MIN_WIDTH = 560

/*
  Außerhalb der Seite, damit es bei jedem Rendern DASSELBE Objekt ist (#303): `useSensor` merkt sich die Optionen nach
  Identität. Ein frisches Objekt je Rendern gab dem `DndContext` neue Sensoren, und jede Zeile mit `useSortable` renderte
  bei jedem Pfeil neu (gemessen 10.10.2026: alle 85 geladenen Zeilen).
*/
const POINTER_ACTIVATION = { activationConstraint: { distance: 5 } }

export const UpcomingPage: React.FC<UpcomingPageProps> = ({ lifeSpace }) => {
  const { t } = useTranslation()
  const isActive = useIsActiveTab()

  // ── Today (frozen per mount; midnight-refresh would invalidate initial state). ──
  const today = useMemo(() => startOfMonth(new Date()), [])

  // Single subscription to the midnight timer so all day rows update
  // the "today" highlight in sync without each DayGroup subscribing
  // independently (used to be N parallel setTimeout listeners).
  useMidnightRefresh()
  const todayDateKey = useMemo(() => dateKey(startOfDay(new Date())), [])

  // ── Local state ──
  const [selectedMonth, setSelectedMonth] = useState<Date>(today)
  const [visibleMonthKey, setVisibleMonthKey] = useState<string>(() => monthKey(today))

  // Ref mirror for IO — useVisibleMonth reads `anchorsRef.current` which is a
  // Map populated by each MonthBlock on mount.
  const anchorsRef = useRef<Map<string, HTMLElement | null>>(new Map())
  const scrollRef = useRef<HTMLDivElement>(null)
  const scrollInnerRef = useRef<HTMLDivElement>(null)
  // Die **Bildlaufleiste** (CONTEXT.md): unsichtbar in Ruhe, sichtbar beim
  // Scrollen, wachgehalten von der Mausbewegung.
  const { visible: scrollbarVisible, onScroll: handleScrollbar } =
    useScrollbarVisibility({ ref: scrollRef })

  // ── Lazy range ──
  // Small initial window — cold-mount of UpcomingPage previously committed
  // 7 MonthBlocks × ~30 DayGroups × N DraggableTaskEntry hooks in a single
  // synchronous pass (measured: 438 ms nested-update). Three months are
  // plenty to cover the scroll-to-today viewport; the auto-fill effect
  // below + scroll triggers extend the range outward on demand.
  const { range, extendUp, extendDown, resetTo } = useVisibleMonthRange({
    initialCenter: today,
    initialPadding: 1,
  })

  // ── Completion grace window ──
  const { graceIds, markCompleted } = useCompletionGrace()
  const dataSource = useDataSource()

  // ── Data ──
  // `setOptimisticTaskOverride` lets badge updates AND drag-and-drop
  // render the change instantly instead of waiting on the IDB write →
  // LiveQuery → re-render roundtrip (~1–2s with sync cycles in flight).
  // The hook owns and auto-clears each override key once Dexie confirms.
  const { months, setOptimisticTaskOverride } = useUpcomingTasks({
    lifeSpace,
    startMonth: range.start,
    endMonth: range.end,
    graceIds,
    isActive,
  })

  // ── Flat snapshot of all rendered tasks (for in-memory lookups) ──
  // Used by useTaskOperations so the optimistic toggle / delete can
  // read each task's current `status` without an extra `getTask`.
  const flatTasks = useMemo(() => {
    const out: Task[] = []
    for (const m of months) for (const d of m.days) for (const tk of d.tasks) out.push(tk)
    return out
  }, [months])

  // ── Task ops ──
  // Wired with the optimistic-override sink so toggle/inline-edit/delete
  // paint in the next paint instead of waiting on the IDB → LiveQuery
  // roundtrip. The grace-window wrapper for toggle stays so completed
  // tasks linger briefly before disappearing from the Upcoming list.
  const { handleUpdateTask, handleToggleComplete, handleDeleteTask } = useTaskOperations({
    t, setOptimisticTaskOverride, tasks: flatTasks,
  })

  /** **Play** — beginnt einen Fokus an dieser Aufgabe (Zeile, Menü). */
  const handlePlayTask = usePlayTask()

  const wrappedToggleComplete = useCallback((taskId: string) => {
    markCompleted(taskId)
    handleToggleComplete(new Set([taskId]))
  }, [handleToggleComplete, markCompleted])

  const wrappedDelete = useCallback((taskId: string) => {
    handleDeleteTask(new Set([taskId]))
  }, [handleDeleteTask])

  /*
    Die Mengen-Fassungen für Leertaste und ⌫ (Abnahme F-DN2). Sie gehen
    bewusst NICHT über die Einzel-Griffe in einer Schleife: `handleToggleComplete`
    und `handleDeleteTask` nehmen von Haus aus eine Menge und machen daraus EINE
    Meldung — dreimal einzeln aufgerufen stünden drei Rückgängig-Meldungen da.
  */
  const toggleCompleteMany = useCallback((ids: ReadonlySet<string>) => {
    if (ids.size === 0) return
    for (const id of ids) markCompleted(id)
    handleToggleComplete(new Set(ids))
  }, [handleToggleComplete, markCompleted])

  const deleteMany = useCallback((ids: ReadonlySet<string>) => {
    if (ids.size === 0) return
    handleDeleteTask(new Set(ids))
  }, [handleDeleteTask])

  // Inline badge update (start date, deadline, duration, attention, tags).
  // Optimistic-first so the badge change is visible in the next paint
  // instead of waiting on the IDB → LiveQuery roundtrip (~1–2s).
  const handleBadgeUpdate = useCallback(async (taskId: string, updates: Partial<Omit<Task, 'id'>>) => {
    setOptimisticTaskOverride(taskId, updates)
    await dataSource.updateTask(taskId, updates)
  }, [setOptimisticTaskOverride, dataSource])

  // ── Editing state (page-wide so only one task is expanded at a time) ──
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null)
  const startEdit = useCallback((taskId: string) => setEditingTaskId(taskId), [])
  const cancelEdit = useCallback(() => setEditingTaskId(null), [])

  /*
    **Die Auswahl ist eine Menge** (Abnahme F-DN2, 22.09.2026). Bis dahin
    führte die Seite genau eine Kennung; ⌘ und ⇧ taten nichts, während jede
    andere Aufgabenliste sie kennt. `useUpcomingSelection` steht weiter unten,
    es braucht `flatTaskIds` — die Reihenfolge, in der ⇧ einen Bereich spannt.
  */

  // ── Inline task creation (one creator visible at a time, anchored to a day) ──
  // New tasks from Demnächst are created as orphan tasks in the current
  // lifespace (projectId == null, lifeSpace == X). They used to be routed
  // through the quick-notes system project which has since been retired.
  const [creatingDateKey, setCreatingDateKey] = useState<string | null>(null)
  // Nach dem Anlegen: die Kennung, bis sie in `flatTaskIds` auftaucht — dann
  // greift der Effekt unten (Auswahl + ins Bild rollen), siehe Issue #154.
  const [pendingSelectId, setPendingSelectId] = useState<string | null>(null)

  const handleStartCreate = useCallback((dateKeyStr: string) => {
    setCreatingDateKey(dateKeyStr)
    setEditingTaskId(null) // only one open editor/creator at a time
  }, [])

  const handleCancelCreate = useCallback(() => setCreatingDateKey(null), [])

  const handleSubmitCreate = useCallback(
    async (dateKeyStr: string, data: TaskCreationData) => {
      const [y, mo, d] = dateKeyStr.split('-').map(Number)
      if (Number.isNaN(y) || Number.isNaN(mo) || Number.isNaN(d)) return
      const groupDay = new Date(y, mo - 1, d)
      const id = crypto.randomUUID()
      // Der Tag der Gruppe schlägt ein im Wann gewähltes Datum — die Uhrzeit bleibt. Ein Datum aus dem
      // Titel („morgen“) schlägt dagegen die Gruppe (#234): Es ist das, was man eben getippt hat.
      const startDate = data.startFromTitle && data.startDate ? data.startDate : groupDay
      const { target } = data
      const place = target
        ? { lifeSpace: target.lifeSpace, projectId: target.projectId, sectionId: target.sectionId, order: await orderAtEndOf(dataSource, target.projectId, target.sectionId) }
        : { lifeSpace, projectId: null, sectionId: null }
      const created = await dataSource.createTask({ ...toCreateTask(data, { id, ...place }), startDate })
      setCreatingDateKey(null)
      // In einem anderen Bereich steht sie hier nicht — dann sagt ein Toast, wo.
      if (target && target.lifeSpace !== lifeSpace) showCreatedElsewhereToast(t, target.projectName, created)
      else setPendingSelectId(id)
    },
    [lifeSpace, dataSource, t],
  )

  // ── Keyboard navigation (Arrow Up/Down + Enter) ──
  // Flat list of task IDs in render order — the traversal set for arrow keys.
  const flatTaskIds = useMemo<string[]>(() => {
    const ids: string[] = []
    for (const m of months) {
      for (const d of m.days) {
        for (const task of d.tasks) ids.push(task.id)
      }
    }
    return ids
  }, [months])

  const selection = useUpcomingSelection(flatTaskIds)
  const { selectedIds, focusedId } = selection
  const selectTask = selection.selectOnly
  const clearSelection = selection.clear
  const moveSelectionTo = selection.moveTo

  // Click outside any TaskEntry clears the selection (matches ProjectPage).
  // Skipping when a dropdown portal is open — those live outside our DOM
  // subtree and their pointerdowns shouldn't bubble into a deselect.
  const handlePagePointerDown = useCallback((e: React.PointerEvent) => {
    const target = e.target as HTMLElement
    if (target.closest('.task-entry')) return
    if (document.querySelector('[data-radix-popper-content-wrapper]')) return
    if (document.querySelector('[data-task-dropdown]')) return
    clearSelection()
  }, [clearSelection])


  /*
    **Die Auswahl geht pro Tag nach unten, nicht als eine große Menge.**

    `DayGroup` ist `React.memo`. Reichte die Seite `selectedIds` durch,
    wechselte deren Identität bei JEDER Auswahländerung — und alle ~60
    sichtbaren Tage rendern neu, samt ihrer Zeilen und Dropdowns. Genau die
    Falle, die weiter unten schon für `editingTaskId` beschrieben ist.

    Also bekommt jeder Tag seine eigene kleine Menge, und alle Tage ohne
    Auswahl teilen sich dieselbe eingefrorene `EMPTY_SELECTION` — dasselbe
    Muster wie `EMPTY_TASKS` in `upcomingTasks.utils.ts`. Zusätzlich wird die
    Menge eines Tages wiederverwendet, solange ihr Inhalt gleich bleibt, damit
    ein Datenwechsel anderswo die Auswahl nicht scheinbar neu macht.
  */
  const prevSelectedByDayRef = useRef<Map<string, ReadonlySet<string>>>(new Map())
  const selectedIdsByDay = useMemo(() => {
    const prev = prevSelectedByDayRef.current
    const next = new Map<string, ReadonlySet<string>>()
    for (const m of months) {
      for (const d of m.days) {
        let picked: Set<string> | null = null
        for (const task of d.tasks) {
          if (selectedIds.has(task.id)) (picked ??= new Set()).add(task.id)
        }
        if (!picked) {
          next.set(d.dateKey, EMPTY_SELECTION)
          continue
        }
        const before = prev.get(d.dateKey)
        const same = before !== undefined
          && before.size === picked.size
          && [...picked].every(id => before.has(id))
        next.set(d.dateKey, same ? before : picked)
      }
    }
    prevSelectedByDayRef.current = next
    return next
  }, [months, selectedIds])

  // Precomputed "which month owns which task" index. Used by the MonthBlock
  // render below so the O(days × tasks) `.some()` scan per month becomes an
  // O(1) `Set.has` lookup on `editingTaskId` — matters on arrow-key
  // navigation where selection flips every keystroke.
  const loadedTaskIds = useMemo(() => months.flatMap(m => m.days.flatMap(d => d.tasks.map(task => task.id))), [months])
  const taskIdsByMonth = useMemo(() => {
    const map = new Map<string, Set<string>>()
    for (const m of months) {
      const set = new Set<string>()
      for (const d of m.days) {
        for (const task of d.tasks) set.add(task.id)
      }
      map.set(m.monthKey, set)
    }
    return map
  }, [months])

  // Parallel index for the creator: which month owns which day-key.
  const dayKeysByMonth = useMemo(() => {
    const map = new Map<string, Set<string>>()
    for (const m of months) {
      const set = new Set<string>()
      for (const d of m.days) set.add(d.dateKey)
      map.set(m.monthKey, set)
    }
    return map
  }, [months])

  // Scroll just enough to bring `taskId` into the "safe" band — outside both
  // fade overlays plus a breathing margin. Using `scrollIntoView({ block:
  // 'nearest' })` would leave the row flush with the viewport edge, where
  // the fade gradient drowns it out.
  //
  // Smooth-scroll only when the delta is large enough that an instant jump
  // would feel jarring (~one row-height). For single-row arrow-key hops the
  // scroll is instant so rapid key presses don't stack a queue of ongoing
  // smooth-scrolls that visually trail behind the user's key input.
  const SMOOTH_SCROLL_THRESHOLD_PX = 60
  const scrollTaskIntoView = useCallback((taskId: string) => {
    const scrollEl = scrollRef.current
    if (!scrollEl) return
    const taskEl = scrollEl.querySelector<HTMLElement>(`[data-task-id="${taskId}"]`)
    if (!taskEl) return

    const rootRect = scrollEl.getBoundingClientRect()
    const taskRect = taskEl.getBoundingClientRect()
    const topSafe = rootRect.top + FADE_HEIGHT_PX + KEYBOARD_SCROLL_MARGIN_PX
    const bottomSafe = rootRect.bottom - FADE_HEIGHT_PX - KEYBOARD_SCROLL_MARGIN_PX

    let delta = 0
    if (taskRect.top < topSafe) {
      delta = taskRect.top - topSafe
    } else if (taskRect.bottom > bottomSafe) {
      delta = taskRect.bottom - bottomSafe
    }

    if (delta !== 0) {
      const behavior: ScrollBehavior =
        Math.abs(delta) > SMOOTH_SCROLL_THRESHOLD_PX ? 'smooth' : 'auto'
      scrollEl.scrollTo({ top: scrollEl.scrollTop + delta, behavior })
    }
  }, [])

  // Neu angelegte Aufgabe auswählen und ins Bild rollen, sobald sie in
  // `flatTaskIds` auftaucht (Issue #154). Bei einem vorher leeren Monat
  // schaltet `MonthBlock` erst dann vom Anlege-Knopf auf alle Tage des
  // Monats um — der Scrollstand bleibt sonst am alten Fleck stehen. Gilt für
  // jede Neuanlage, nicht nur im leeren Monat.
  useEffect(() => {
    if (!pendingSelectId) return
    if (!flatTaskIds.includes(pendingSelectId)) return
    selectTask(pendingSelectId)
    scrollTaskIntoView(pendingSelectId)
    setPendingSelectId(null)
  }, [pendingSelectId, flatTaskIds, selectTask, scrollTaskIntoView])

  // Tab / Shift+Tab in expanded mode: save current edit (TaskEntry does that
  // itself before calling onNavigateNext/Prev) and open the adjacent task.
  // Boundaries close the editor instead of wrapping.
  //
  // We read `flatTaskIds` through a ref instead of as a useCallback dep, so
  // this callback keeps a stable identity across renders. Otherwise it would
  // change on every months/flatTaskIds rebuild and propagate through
  // MonthBlock → DayGroup as a "prop changed" re-render across the whole
  // page (60+ DayGroups), which we measured at ~960 wasted DayGroup renders
  // per single mutation.
  const flatTaskIdsRef = useRef<string[]>(flatTaskIds)
  useEffect(() => { flatTaskIdsRef.current = flatTaskIds }, [flatTaskIds])
  const handleNavigateEdit = useCallback(
    (taskId: string, direction: 'next' | 'prev') => {
      const ids = flatTaskIdsRef.current
      const idx = ids.indexOf(taskId)
      if (idx === -1) {
        setEditingTaskId(null)
        return
      }
      const nextIdx = direction === 'next' ? idx + 1 : idx - 1
      if (nextIdx < 0 || nextIdx >= ids.length) {
        setEditingTaskId(null)
        return
      }
      const nextId = ids[nextIdx]
      setEditingTaskId(nextId)
      selectTask(nextId)
      scrollTaskIntoView(nextId)
    },
    [scrollTaskIntoView, selectTask],
  )

  // First task whose row currently intersects the scroll viewport — the
  // starting point when arrow keys are pressed without an existing selection.
  const findFirstVisibleTaskId = useCallback((): string | null => {
    const scrollEl = scrollRef.current
    if (!scrollEl || flatTaskIds.length === 0) return null
    const rootRect = scrollEl.getBoundingClientRect()
    for (const id of flatTaskIds) {
      const el = scrollEl.querySelector<HTMLElement>(`[data-task-id="${id}"]`)
      if (!el) continue
      const r = el.getBoundingClientRect()
      if (r.bottom > rootRect.top && r.top < rootRect.bottom) return id
    }
    return flatTaskIds[0] ?? null
  }, [flatTaskIds])

  /*
    **Die Aufgaben-Details** (Abnahme 21.09.2026, F-DN3): Es gab sie nur im
    Projekt. Seit F-DN2 (22.09.) führt diese Seite eine Menge wie die anderen,
    der Griff bekommt sie also unverändert — `detailsValues.ts` kennt den
    Mehrfach-Fall bereits.

    Die Tastatur gehört dem Fenster (`window`-Listener oben), nicht einer
    Hülle: „zurück an die Liste" heißt hier, den Fokus aus dem Knopf zu nehmen,
    damit die Eingabetaste nicht wieder ihn trifft.

    Die Pfeile IN den Details gehen weiter Zeile für Zeile — dort steht eine
    Aufgabe im Blatt, ein Bereich wäre dort ohne Bedeutung.
  */
  const stepSelection = useCallback((direction: 'prev' | 'next') => {
    const nextId = selection.step(direction, false)
    if (nextId) scrollTaskIntoView(nextId)
  }, [selection, scrollTaskIntoView])

  /*
    ⌫ und das Menü „Aufgaben“ (#257): die Auswahl löschen und den Fokus auf die Aufgabe hinter
    der letzten setzen (oder vor die erste, wenn sie am Ende standen). Dasselbe Muster wie in
    `useTaskListInteraction`; `deleteMany` zeigt EINE Rückgängig-Meldung für alle.
  */
  const deleteSelection = useCallback(() => {
    if (selectedIds.size === 0) return
    const remaining = flatTaskIds.filter(id => !selectedIds.has(id))
    const lastIdx = flatTaskIds.reduce((acc, id, i) => (selectedIds.has(id) ? i : acc), -1)
    const nextId =
      flatTaskIds.slice(lastIdx + 1).find(id => !selectedIds.has(id))
      ?? remaining[remaining.length - 1]
      ?? null
    deleteMany(selectedIds)
    selection.moveTo(nextId)
    if (nextId) scrollTaskIntoView(nextId)
  }, [selectedIds, flatTaskIds, deleteMany, selection, scrollTaskIntoView])

  // Dieselbe Zahl gilt auch ohne Details (#296): Das Fenster darf sonst bis auf eine Aufgabenliste schrumpfen, und
  // die Zeitleiste verlöre ihre Titel. Fenster und Navigation richten sich danach (`MainLayout`).
  const { setPageListMinWidth, clearPageListMinWidth } = useCanvasFocus()
  const tabId = useTabId()
  useLayoutEffect(() => {
    setPageListMinWidth(tabId, UPCOMING_LIST_MIN_WIDTH)
    return () => clearPageListMinWidth(tabId)
  }, [tabId, setPageListMinWidth, clearPageListMinWidth])

  const details = useTaskDetailsPane({
    tasks: flatTasks,
    navigation: {
      selectedTaskIds: selectedIds as Set<string>,
      selectTask: (taskId: string) => {
        if (!flatTaskIdsRef.current.includes(taskId)) return false
        selectTask(taskId)
        return true
      },
      stepTask: stepSelection,
      canStep: direction => {
        const ids = flatTaskIdsRef.current
        const idx = focusedId ? ids.indexOf(focusedId) : -1
        if (idx === -1) return ids.length > 0
        return direction === 'next' ? idx < ids.length - 1 : idx > 0
      },
      focusList: () => (document.activeElement as HTMLElement | null)?.blur(),
      topVisibleTaskId: () => (scrollRef.current ? topVisibleTaskId(scrollRef.current) : null),
    },
    setOptimisticTaskOverride,
    listMinWidth: UPCOMING_LIST_MIN_WIDTH,
  })
  const showDetailsRef = useRef(details.show)
  showDetailsRef.current = details.show

  useEffect(() => {
    // Inactive tabs stay mounted in TabStack — skip listener entirely so
    // arrow/enter keys don't fire on hidden pages.
    if (!isActive) return
    const onKeyDown = (e: KeyboardEvent) => {
      // Defer entirely to TaskEntry while a task is being edited.
      if (editingTaskId) return
      // Ignore when the user is typing somewhere else (title input, etc.) or
      // a portal-based menu/dropdown is open.
      const target = e.target as HTMLElement | null
      const tag = target?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return
      if (document.querySelector('[role="menu"]')) return
      // ⇧ darf durch — es erweitert den Bereich (F-DN2).
      if (e.metaKey || e.ctrlKey || e.altKey) return

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (flatTaskIds.length === 0) return
        e.preventDefault()
        // Noch nichts gewählt — oder die gewählte Zeile ist weg (abgehakt und
        // aus der Liegezeit gefallen, von der Cloud entfernt): bei der ersten
        // sichtbaren anfangen statt am Listenanfang.
        if (focusedId === null || flatTaskIds.indexOf(focusedId) === -1) {
          const first = findFirstVisibleTaskId()
          if (first) {
            selectTask(first)
            scrollTaskIntoView(first)
          }
          return
        }
        const nextId = selection.step(e.key === 'ArrowDown' ? 'next' : 'prev', e.shiftKey)
        if (nextId) scrollTaskIntoView(nextId)
        return
      }

      // Aufklappen bleibt einzeln: Es gibt genau eine offene Karte. Bei
      // mehreren Ausgewählten öffnet Enter die Zeile unter dem Cursor.
      if (e.key === 'Enter' && focusedId) {
        e.preventDefault()
        startEdit(focusedId)
        return
      }

      // Space → toggle complete on the selected tasks.
      // Matches useTaskListInteraction / useProjectNavigation semantics.
      if (e.code === 'Space' && selectedIds.size > 0) {
        e.preventDefault()
        toggleCompleteMany(selectedIds)
        return
      }

      if (e.key === 'Escape' && selectedIds.size > 0) {
        e.preventDefault()
        clearSelection()
        return
      }

      if ((e.key === 'Backspace' || e.key === 'Delete') && selectedIds.size > 0) {
        e.preventDefault()
        deleteSelection()
        return
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isActive, editingTaskId, selectedIds, focusedId, selection, selectTask, flatTaskIds, findFirstVisibleTaskId, scrollTaskIntoView, startEdit, clearSelection, deleteSelection, toggleCompleteMany])

  // ── Visible month via IntersectionObserver ──
  // Dedup by key — setSelectedMonth(new Date(...)) creates a fresh object
  // reference every call, and React uses Object.is, so without this guard
  // the MutationObserver + scroll listener would re-trigger setState every
  // frame even when the visible month hasn't changed, blowing the update-
  // depth limit.
  //
  // `programmaticTargetRef` is a navigation lock: chip-click and Cmd+Arrow
  // call `resetTo(month)` which mutates the lazy range. Mounting/unmounting
  // MonthBlocks shifts content above the user's scrollTop, so the IO
  // recompute that fires before our rAF scroll lands sees the wrong month
  // at the top and would otherwise reset `selectedMonth` back. We ignore
  // those bogus reports until the IO confirms the target month at least
  // once OR the safety timeout elapses (in case the target never becomes
  // visible — e.g. user scrolled away mid-navigation).
  const programmaticTargetRef = useRef<string | null>(null)
  const programmaticTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleVisibleChange = useCallback((key: string) => {
    const target = programmaticTargetRef.current
    if (target) {
      if (key === target) {
        // Confirmed — release the lock.
        programmaticTargetRef.current = null
        if (programmaticTimerRef.current) {
          clearTimeout(programmaticTimerRef.current)
          programmaticTimerRef.current = null
        }
      } else {
        // Suppress: the IO is reading a transient pre-scroll layout.
        return
      }
    }
    setVisibleMonthKey(prev => (prev === key ? prev : key))
    const [y, m] = key.split('-').map(Number)
    if (Number.isNaN(y) || Number.isNaN(m)) return
    setSelectedMonth(prev => {
      if (prev.getFullYear() === y && prev.getMonth() === m - 1) return prev
      return new Date(y, m - 1, 1)
    })
  }, [])

  /** Engage the navigation lock for a single programmatic month-jump.
   *  Suppresses the IO-driven `handleVisibleChange` and the lazy-extend-
   *  on-scroll listener while the page settles on the requested target. */
  const armProgrammaticNavigation = useCallback((targetKey: string) => {
    programmaticTargetRef.current = targetKey
    if (programmaticTimerRef.current) clearTimeout(programmaticTimerRef.current)
    programmaticTimerRef.current = setTimeout(() => {
      programmaticTargetRef.current = null
      programmaticTimerRef.current = null
    }, 800)
    // Reset the auto-fill watermark so the new (potentially shorter)
    // range is allowed to extend down again until the viewport is full.
    // Otherwise the cumulative `prev > current` check from the previous
    // wider range would block extension forever — leaving a half-empty
    // page after navigating to a sparse month.
    prevAutoFillHeightRef.current = 0
  }, [])

  // Drop the timer on unmount.
  useEffect(() => () => {
    if (programmaticTimerRef.current) clearTimeout(programmaticTimerRef.current)
  }, [])

  useVisibleMonth({ scrollRef, anchorsRef, onChange: handleVisibleChange })

  // ── Lazy extension on scroll ──
  // Suppressed while a programmatic-navigation lock is engaged so the
  // scroll events fired by chip/keyboard navigation can't trigger
  // extendUp/extendDown — those would shift the DOM and yank the user's
  // viewport off the target month they just navigated to.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const onScroll = () => {
      if (programmaticTargetRef.current) return
      if (el.scrollTop < LAZY_LOAD_THRESHOLD_PX) extendUp()
      if (el.scrollTop + el.clientHeight > el.scrollHeight - LAZY_LOAD_THRESHOLD_PX) {
        extendDown()
      }
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [extendUp, extendDown])

  // ── Die Stelle halten, wenn oberhalb Inhalt wächst ──
  // Hier stand ein Ausgleich, der nur griff, wenn `range.start` nach vorn
  // rückte. Die neuen Monate stehen dann aber erst als Leer-Zustand da, weil
  // ihre Abfrage noch läuft, und klappen einen Commit später auf — dieses
  // Aufklappen schob die Liste weg („Heute" landete im Juni, 23.09.2026).
  // `useScrollAnchor` hält stattdessen ein Element fest, egal was wächst.
  // Steht VOR dem Auto-fill: Der misst den Rest unter dem Bild erst, wenn der
  // Anker zurückgerückt ist.
  const { pin: pinScrollAnchor, restore: restoreScrollAnchor } = useScrollAnchor({
    scrollRef,
    contentRef: scrollInnerRef,
    isActive,
  })
  useLayoutEffect(() => {
    restoreScrollAnchor()
  }, [months, restoreScrollAnchor])

  // ── Initial scroll position: today's day row ──
  // Runs synchronously with the commit that renders the first non-empty
  // month list. Mirrors `handleJumpToday`'s positioning: day row lands just
  // below the top fade overlay (instead of the month anchor), so the user
  // lands on "today" on mount — same behaviour as clicking the today chip.
  // MonthBlock registers its anchor via a ref callback (not useEffect), so
  // `anchorsRef` and the day `<div data-date="…">` elements are both already
  // in the DOM at this point — no rAF dance needed. Hard jump (no smooth
  // behaviour) on mount so the page appears already at the right position
  // instead of animating from the top.
  const didInitialScrollRef = useRef(false)
  useLayoutEffect(() => {
    if (didInitialScrollRef.current) return
    if (months.length === 0) return
    const el = scrollRef.current
    if (!el) return
    const todayMonthKey = monthKey(today)
    const todayKey = todayDateKey
    const dayEl = el.querySelector<HTMLElement>(`[data-date="${todayKey}"]`)
    if (dayEl) {
      const rootRect = el.getBoundingClientRect()
      const dayRect = dayEl.getBoundingClientRect()
      el.scrollTop += (dayRect.top - rootRect.top) - FADE_HEIGHT_PX - KEYBOARD_SCROLL_MARGIN_PX
      pinScrollAnchor(dayEl, FADE_HEIGHT_PX + KEYBOARD_SCROLL_MARGIN_PX)
    } else {
      // Fallback — today's row not yet mounted for some reason: align on
      // today's month anchor instead.
      const anchor = anchorsRef.current.get(todayMonthKey)
      if (!anchor) return
      scrollMonthIntoPosition(el, anchor)
      pinScrollAnchor(anchor, monthJumpOffset(anchor))
    }
    // Pin title + chip highlight to today so the scroll-driven recompute
    // doesn't flash the previous month before settling.
    setVisibleMonthKey(todayMonthKey)
    didInitialScrollRef.current = true
  }, [months, today, todayDateKey, pinScrollAnchor])

  // ── Auto-fill: keep extending down until there's room below the viewport ──
  // Without this, a tall window could show empty space below the last month
  // because onScroll never fires for content that's never been scrollable.
  // Gemessen wird der Rest UNTER dem sichtbaren Bereich, nicht die Gesamthöhe:
  // Ist der Zielmonat leer (Zuhause, 23.09.2026), steht er nach dem Laden fast
  // am Ende der Liste und lässt sich nicht nach oben scrollen — die Seite blieb
  // im August hängen. So wächst die Liste, bis der Anker sein Ziel erreicht.
  // Steht deshalb NACH dem Anfangs-Sprung: Im selben Commit misst er schon die
  // neue Stelle, und `extendDown` im Layout-Effekt rendert vor dem Malen nach —
  // die geklemmte Zwischenstellung ist nie zu sehen.
  // We gate the recursion on scrollHeight growth so a pathological month
  // producing zero pixels can't infinite-loop.
  //
  // Must be skipped when the tab is inactive inside TabStack — hidden slots
  // have `clientHeight === 0`, which makes `needsMore` permanently true and
  // sends extendDown into an infinite-setState loop (Maximum update depth).
  const prevAutoFillHeightRef = useRef(0)
  useLayoutEffect(() => {
    if (!isActive) return
    const el = scrollRef.current
    if (!el || months.length === 0) return
    if (el.clientHeight === 0) return // inactive/hidden slot, nothing to fill
    const needsMore = el.scrollHeight - el.scrollTop - el.clientHeight < LAZY_LOAD_THRESHOLD_PX
    const grew = el.scrollHeight > prevAutoFillHeightRef.current
    if (needsMore && grew) {
      prevAutoFillHeightRef.current = el.scrollHeight
      extendDown()
    }
  }, [isActive, months, extendDown])

  // ── Chip click: jump to the 1st of the chosen month in the day list ──
  const handleChipSelect = useCallback((month: Date) => {
    const targetKey = monthKey(month)
    armProgrammaticNavigation(targetKey)
    setSelectedMonth(month)
    setVisibleMonthKey(targetKey)
    // Padding=6 → 13-month window centred on the target. Empty months are
    // ~120px tall, so this guarantees the scroll container has enough
    // content to overflow even when the user lands deep in a task-free
    // region (otherwise only the 3 padded months render and the page
    // ends up un-scrollable until the user reaches a populated month).
    resetTo(month, 6)
    // Double rAF so the scroll lands AFTER the auto-fill useLayoutEffect
    // cascade has settled. resetTo can shrink the range to a sparse window
    // (e.g. far-back months with no tasks); auto-fill then extendDowns for
    // multiple commits until the viewport is full. A single rAF fires
    // mid-cascade and lands on a soon-to-be-stale layout; the second rAF
    // is queued after the cascade has stabilized.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const el = scrollRef.current
      const anchor = anchorsRef.current.get(targetKey)
      if (!el || !anchor) return
      scrollMonthIntoPosition(el, anchor)
      pinScrollAnchor(anchor, monthJumpOffset(anchor))
    }))
  }, [resetTo, armProgrammaticNavigation, pinScrollAnchor])

  /*
    **Sprung aus „Favoriten“** (#210): eine datierte Aufgabe ohne Projekt. Liegt ihr Monat außerhalb
    der gezeichneten Monate, springt die Seite erst dorthin (wie der Monats-Chip). Ausgewählt, gerollt
    und in den Details geöffnet wird erst, wenn die Seite fertig geladen ist — Demnächst lädt danach
    noch Monate nach, und eine früh gerollte Aufgabe rutschte wieder weg (gemeldet 01.10.2026).
  */
  const [jumpSignal, setJumpSignal] = useState(0)
  useEffect(() => onAppEvent('pending-task-jump', () => setJumpSignal(n => n + 1)), [])
  // Über Refs: Hinge der Effekt an ihnen, bräche sein Aufräumer den eigenen Sprung ab.
  const rangeRef = useRef(range)
  rangeRef.current = range
  const chipSelectRef = useRef(handleChipSelect)
  chipSelectRef.current = handleChipSelect
  const selectTaskRef = useRef(selectTask)
  selectTaskRef.current = selectTask
  const pinScrollAnchorRef = useRef(pinScrollAnchor)
  pinScrollAnchorRef.current = pinScrollAnchor
  useEffect(() => {
    if (!isActive) return
    const pending = consumePendingPageJump('upcoming', lifeSpace)
    if (!pending) return
    let cancelled = false
    let done = false
    let cancelSettle: (() => void) | undefined
    void dataSource.getTask(pending.taskId).then(task => {
      const date = task?.startDate ?? task?.dueDate
      if (cancelled || !task || !date) return
      const month = startOfMonth(new Date(date))
      const { start, end } = rangeRef.current
      if (month < start || month > end) chipSelectRef.current(month)
      cancelSettle = whenTaskRowSettled({
        root: () => scrollRef.current,
        taskId: task.id,
        run: row => {
          done = true
          selectTaskRef.current(task.id)
          // Festhalten, nicht nur hinrollen: Sonst rückt der Anker von „Heute“ die Liste zurück,
          // sobald die Details aufgehen oder Monate nachladen.
          const scrollEl = scrollRef.current
          if (scrollEl) {
            scrollRowAndPin({ scrollEl, row, pin: pinScrollAnchorRef.current, minOffset: FADE_HEIGHT_PX + KEYBOARD_SCROLL_MARGIN_PX })
          }
          if (pending.openDetails) showDetailsRef.current()
        },
      })
    })
    return () => {
      cancelled = true
      cancelSettle?.()
      // Abgebaut, bevor es lief — so beim Einhängen im Entwicklungsmodus (StrictMode): Ohne das
      // fand der zweite Lauf den Merker leer, und der Sprung auf eine frisch geöffnete Seite ging
      // verloren (gemeldet 01.10.2026).
      if (!done) putBackPageJump('upcoming', lifeSpace, pending)
    }
  }, [isActive, lifeSpace, dataSource, jumpSignal])

  // ── Anchor registration ──
  const registerAnchor = useCallback((key: string, el: HTMLElement | null) => {
    if (el) anchorsRef.current.set(key, el)
    else anchorsRef.current.delete(key)
  }, [])

  // ── Derive visible month Date from key for MonthTitle ──
  const visibleMonth = useMemo(() => {
    const [y, m] = visibleMonthKey.split('-').map(Number)
    if (Number.isNaN(y) || Number.isNaN(m)) return today
    return new Date(y, m - 1, 1)
  }, [visibleMonthKey, today])

  // ── Task counts per month (for chip badges) ──
  // Derived only from months currently in the lazy-load window. Chips outside
  // that window show no count until the user scrolls them into view.
  const taskCountsByMonth = useMemo<Record<string, number>>(() => {
    const counts: Record<string, number> = {}
    for (const m of months) {
      let total = 0
      for (const day of m.days) total += day.tasks.length
      counts[m.monthKey] = total
    }
    return counts
  }, [months])

  // ── Today memo: drives both the chip marker and Cmd+T ──
  const todayMonth = useMemo(() => startOfMonth(new Date()), [])

  // ── Jump to today: reset range, align anchor + chip selection, scroll the
  // day row into view below the top fade, and select the first task of the
  // day if there is one.
  const handleJumpToday = useCallback(() => {
    const now = new Date()
    const todayStartDate = startOfDay(now)
    const todayMonthDate = startOfMonth(now)
    const todayKeyStr = dateKey(todayStartDate)
    const todayMonthKeyStr = monthKey(todayMonthDate)
    armProgrammaticNavigation(todayMonthKeyStr)
    setSelectedMonth(todayMonthDate)
    setVisibleMonthKey(todayMonthKeyStr)
    // Padding=6 → guarantees a scrollable viewport even when surrounding
    // months are empty. See handleChipSelect comment for rationale.
    resetTo(todayMonthDate, 6)

    // Auto-select the first task of today (if any) so keyboard nav can pick
    // up right away. `months` here is the snapshot before the resetTo render
    // commits — that's fine, today's bucket is still in it because the lazy
    // range always includes today (initial center, extended outward only).
    const todayBucket = months.find(m => m.monthKey === monthKey(todayMonthDate))
    const todayDay = todayBucket?.days.find(d => d.dateKey === todayKeyStr)
    const firstTodayTaskId = todayDay?.tasks[0]?.id ?? null
    moveSelectionTo(firstTodayTaskId)
    // Defer through two rAFs. `resetTo` may unmount/remount MonthBlocks for
    // the new range; the first rAF lets React commit that, the second lets
    // the MonthBlock ref callbacks register the fresh day rows in the DOM.
    // (Single rAF is sometimes too early and the querySelector misses.)
    const performScroll = () => {
      const scrollEl = scrollRef.current
      if (!scrollEl) return
      const todayKey = dateKey(todayStartDate)
      const dayEl = scrollEl.querySelector<HTMLElement>(`[data-date="${todayKey}"]`)
      if (dayEl) {
        const rootRect = scrollEl.getBoundingClientRect()
        const dayRect = dayEl.getBoundingClientRect()
        const targetTop =
          scrollEl.scrollTop + (dayRect.top - rootRect.top) - FADE_HEIGHT_PX - KEYBOARD_SCROLL_MARGIN_PX
        // Vor dem weichen Scrollen: Klappt unterwegs ein Monat oberhalb auf,
        // springt der Anker gleich ans richtige Ziel statt ans alte.
        pinScrollAnchor(dayEl, FADE_HEIGHT_PX + KEYBOARD_SCROLL_MARGIN_PX)
        if (typeof scrollEl.scrollTo === 'function') {
          scrollEl.scrollTo({ top: targetTop, behavior: 'smooth' })
        } else {
          scrollEl.scrollTop = targetTop
        }
        return
      }
      // Fallback — day row not yet mounted: align on the month anchor instead.
      const anchor = anchorsRef.current.get(monthKey(todayMonthDate))
      if (!anchor) return
      scrollMonthIntoPosition(scrollEl, anchor)
      pinScrollAnchor(anchor, monthJumpOffset(anchor))
    }
    requestAnimationFrame(() => requestAnimationFrame(performScroll))
  }, [resetTo, months, armProgrammaticNavigation, pinScrollAnchor, moveSelectionTo])

  // ── Expand: reserve scroll-room + smooth-scroll into view ──
  // Mirrors TaskList's behaviour (src/renderer/features/taskManagement/
  // components/TaskList/TaskList.tsx). When a row near the bottom edge is
  // opened, its expanded card would clip under the fade overlay; we extend
  // the scrollable content's bottom margin by just enough so `scrollIntoView`
  // can bring the full card into view.
  const hadExpandMarginRef = useRef(false)

  useLayoutEffect(() => {
    if (!editingTaskId) return
    const scrollEl = scrollRef.current
    const innerEl = scrollInnerRef.current
    if (!scrollEl || !innerEl) return
    const wrapper = scrollEl.querySelector<HTMLElement>(`[data-task-id="${editingTaskId}"]`)
    if (!wrapper) return

    const EXPAND_DELTA = 150
    const wrapperRect = wrapper.getBoundingClientRect()
    const scrollRect = scrollEl.getBoundingClientRect()
    const overflow = (wrapperRect.bottom + EXPAND_DELTA) - scrollRect.bottom
    if (overflow <= 0) return

    const remainingScroll = scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight
    if (remainingScroll >= overflow) return

    const marginNeeded = overflow - remainingScroll
    innerEl.style.transition = 'none'
    innerEl.style.marginBottom = `${marginNeeded}px`
    hadExpandMarginRef.current = true
  }, [editingTaskId])

  // Cleanup: once the task collapses, animate the reserved margin back to 0.
  useEffect(() => {
    if (editingTaskId || !hadExpandMarginRef.current) return
    hadExpandMarginRef.current = false
    const innerEl = scrollInnerRef.current
    if (!innerEl) return
    const currentMargin = parseFloat(innerEl.style.marginBottom) || 0
    if (currentMargin <= 0) {
      innerEl.style.marginBottom = ''
      innerEl.style.transition = ''
      return
    }
    innerEl.style.transition = 'margin-bottom 500ms cubic-bezier(0.65, 0, 0.35, 1)'
    const rafId = requestAnimationFrame(() => {
      innerEl.style.marginBottom = '0px'
    })
    const cleanupTimer = setTimeout(() => {
      innerEl.style.transition = ''
      innerEl.style.marginBottom = ''
    }, 520)
    return () => {
      cancelAnimationFrame(rafId)
      clearTimeout(cleanupTimer)
      innerEl.style.transition = ''
    }
  }, [editingTaskId])

  // Smooth-scroll the expanded task into view. Two passes:
  //  - 50ms: start scrolling while the expand animation is still running so
  //    the motion feels continuous with the card opening.
  //  - 300ms: final correction once the expanded card has reached its full
  //    height (framer-motion layout animation has settled).
  useEffect(() => {
    if (!editingTaskId) return
    const scrollEl = scrollRef.current
    if (!scrollEl) return
    const scrollTo = () => {
      const wrapper = scrollEl.querySelector<HTMLElement>(`[data-task-id="${editingTaskId}"]`)
      wrapper?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
    const t1 = setTimeout(scrollTo, 50)
    const t2 = setTimeout(scrollTo, 300)
    return () => {
      clearTimeout(t1)
      clearTimeout(t2)
    }
  }, [editingTaskId])

  // ── Year picker: jump to Jan of the chosen year ──
  const handleYearSelect = useCallback((year: number) => {
    const target = new Date(year, 0, 1)
    handleChipSelect(target)
  }, [handleChipSelect])

  // ── Drag-and-drop: drop a task on another day to re-schedule it ──
  // A 5px activation distance keeps click/double-click intact.
  const dndSensors = useSensors(useSensor(PointerSensor, POINTER_ACTIVATION))

  const [activeDragTaskId, setActiveDragTaskId] = useState<string | null>(null)
  const activeDragTask = useMemo<TaskEntryData | null>(() => {
    if (!activeDragTaskId) return null
    for (const m of months) {
      for (const day of m.days) {
        const t = day.tasks.find(t => t.id === activeDragTaskId)
        if (t) {
          return {
            id: t.id,
            title: t.title,
            completed: t.status === 'completed',
            attention: t.attention,
            description: t.description,
            estimatedDuration: t.estimatedDuration,
            durationMode: t.durationMode,
            startDate: t.startDate,
            startTime: t.startTime,
            startPeriod: t.startPeriod,
            dueDate: t.dueDate,
            dueTime: t.dueTime,
            duePeriod: t.duePeriod,
            tags: t.tags,
          }
        }
      }
    }
    return null
  }, [activeDragTaskId, months])

  // Index of currently-rendered tasks by id — lets handleDragEnd skip the
  // extra `getTask` roundtrip on every drop. The upstream LiveQuery
  // already keeps this in sync; no risk of stale reads during a drag.
  const tasksById = useMemo(() => {
    const map = new Map<string, Task>()
    for (const m of months) for (const d of m.days) for (const t of d.tasks) map.set(t.id, t)
    return map
  }, [months])

  /*
    **Die Kürzel des Aufgaben-Menüs** — `W`, `⇧W`, `D`, `⇧D`, `M`, `T`, `G`.

    Sie fehlten hier bis zum 18.09.2026: `MonthBlock` und `DayGroup` reichten
    beide Props sauber bis zur Zeile durch, nur diese Seite gab sie nie hinein
    und band die Tasten nicht (`.claude/tasks/kuerzel-auf-demnaechst.md`).

    **Der Anker ist die Zeile unter dem Cursor** (`focusedId`). `anchorTaskId()`
    wie auf *Ruhend* braucht es nicht: Dort gibt es zwei Quellen (Tastaturfokus
    der Liste und Auswahl), hier führt die Seite ihre Navigation selbst.

    **Gewirkt wird auf die ganze Auswahl** (Abnahme F-DN2, 22.09.2026): Der
    Wähler öffnet sich an der Zeile unter dem Cursor, was man dort setzt,
    trifft alle ausgewählten — wie in Kurz notiert (Abnahme 1.15).

    **`R` wirkt hier seit dem 21.09.2026** (F-DN4). Bis dahin stand an dieser
    Stelle die alte Regel („`R` bleibt bewusst weg, *Demnächst* zeigt, was
    ansteht"). Sie ist gekippt: Wer ein „Wann" gesetzt hat, will die Aufgabe an
    ihrem Tag sehen, auch wenn sie ruht. Der Griff dazu steht unten.
  */
  const [menuShortcut, setMenuShortcut] = useState<{ taskId: string; target: TaskMenuTarget } | null>(null)
  const closeMenuShortcut = useCallback(() => setMenuShortcut(null), [])
  // „Verschieben …“ aus dem Rechtsklick öffnet denselben Kasten an der Zeile wie ⇧⌘M (#263).
  const openMoveAtRow = useCallback((taskId: string) => setMenuShortcut({ taskId, target: 'move' }), [])

  /* Die Haken im Menü — dieselbe Auswahl, die auch die Kürzel meinen. */
  const upcomingTasks = useMemo(() => [...tasksById.values()], [tasksById])
  const selectedIdsRef = useRef<ReadonlySet<string>>(selectedIds)
  selectedIdsRef.current = selectedIds
  const getContextMenuMarks = useContextMenuMarks({
    getSelection: () => new Set(selectedIdsRef.current),
    tasks: upcomingTasks,
  })

  /*
    Der Wähler am Rechtsklick und an der Marke trifft die **ganze** Auswahl,
    wenn die angeklickte Zeile dazugehört — sonst nur sie. Dieselbe Regel und
    derselbe Griff wie in Kurz notiert, im Projekt und auf Ruhend; der
    Einzelweg `handleBadgeUpdate` bleibt für die Aufgaben-Details und `R`.
  */
  const handleContextMenuBadgeUpdate = useContextMenuBadgeUpdate({
    getSelection: () => new Set(selectedIdsRef.current),
    setOptimisticTaskOverride,
    logTag: 'UpcomingPage',
  })

  /*
    **`R` wirkt auch hier** (Abnahme F-DN4, 21.09.2026). Vorher fehlte dieser
    Griff als einzige der Aufgabenseiten — die Taste tat nichts, und die Zeile
    „Ruhen lassen" stand im Menü trotzdem scharf da. Seit ruhende Aufgaben mit
    Datum hier stehen, gehört beides zusammen: legen und wieder wecken, über
    denselben Kanal wie der Rechtsklick. Welche Richtung gemeint ist, sagt die
    Zeile unter dem Fokus.
  */
  const handleRestShortcut = useCallback((taskId: string) => {
    const anchor = tasksById.get(taskId)
    // Eine Unteraufgabe ruht nicht selbst (T14 #179, Grill 7) — sie ruht mit ihrer Aufgabe.
    if (!anchor || anchor.parentTaskId) return
    // Die Richtung sagt die Zeile unter dem Cursor; angewandt wird sie auf
    // alle Ausgewählten, damit `R` bei Mehrfachauswahl nicht die Hälfte legt
    // und die andere weckt.
    const selected = selectedIdsRef.current
    const targetIds = selected.size > 1 && selected.has(taskId) ? [...selected] : [taskId]
    const now = new Date()
    const shouldWake = anchor.pausedAt != null
    for (const id of targetIds) {
      const task = tasksById.get(id)
      if (!task || task.parentTaskId) continue
      handleBadgeUpdate(id, shouldWake ? wakeTaskUpdate(task, now) : { pausedAt: now })
    }
  }, [tasksById, handleBadgeUpdate])

  // ⇧⌘F (#210): der Stern der Auswahl — mit Meldung und Rückgängig.
  // Die Details schreiben einzeln (s. o.), nur ihr Stern gilt der ganzen Auswahl (#210).
  const handleDetailsUpdate = useMemo(
    () => routeDetailsUpdate({ single: handleBadgeUpdate, selection: handleContextMenuBadgeUpdate }),
    [handleBadgeUpdate, handleContextMenuBadgeUpdate],
  )
  const favoriteShortcut = useFavoriteShortcut({ getSelection: () => new Set(selectedIdsRef.current), applyUpdate: handleContextMenuBadgeUpdate })
  const showTaskInProject = useShowTaskInProject()
  useTaskMenuShortcuts({
    enabled: isActive,
    getAnchorTaskId: () => focusedId,
    onMenuShortcut: (taskId, target) => setMenuShortcut({ taskId, target }),
    onFavoriteShortcut: taskId => { if (taskId) void favoriteShortcut(taskId) },
    onFocusShortcut: handlePlayTask,
    onRestShortcut: handleRestShortcut,
    onDetailsShortcut: details.toggle,
    onShowInProjectShortcut: showTaskInProject,
    canMove: true,
  })
  // **Verschieben …** (#257): Rechtsklick, ⇧⌘M und das Menü „Aufgaben“.
  const moveMenu = useMoveMenu(() => selectedIdsRef.current, openMoveAtRow)

  /* Zeiger-Y, laufend mitgeschrieben. Sie entscheidet beim Ablegen auf eine
     Zeile, ob es davor oder dahinter geht — dasselbe Muster wie im Projekt
     (`useTaskDragAndDrop`). Passiv, damit das Rollen nicht ausgebremst wird. */
  const pointerYRef = useRef(0)
  useEffect(() => {
    const onMove = (e: PointerEvent) => { pointerYRef.current = e.clientY }
    document.addEventListener('pointermove', onMove, { passive: true })
    return () => document.removeEventListener('pointermove', onMove)
  }, [])

  /** Alle Tage der gerenderten Monate, nach Tagesschlüssel. */
  const daysByKey = useMemo(() => {
    const map = new Map<string, DayBucket>()
    for (const m of months) for (const d of m.days) map.set(d.dateKey, d)
    return map
  }, [months])

  const { strategy: dayAwareStrategy, updateGapDirection, resetGapDirection, gapAfterRef } =
    useDayAwareStrategy(months, flatTaskIds)

  /*
    **Wer liegt unter dem Zeiger?** Zeilen per `closestCenter` wie im Projekt; ein **leerer Tag** nur, wenn der Zeiger
    in ihm steht — sonst ließe sich keine Aufgabe auf einen freien Tag legen. Geschichte (22.09. und 10.10.2026) und
    Begründung stehen an `upcomingDropTarget`.
  */
  // Zeilen per `closestCenter`, leere Tage nur unter dem Zeiger (`upcomingDropTarget`, 10.10.2026).
  const dropTarget = useMemo(() => upcomingDropTarget(id => daysByKey.get(id)), [daysByKey])

  const handleDragMove = useCallback((e: DragMoveEvent) => {
    const overId = e.over?.id as string | undefined
    if (!overId || daysByKey.has(overId) || !e.over?.rect) return
    updateGapDirection(e.over.rect, pointerYRef.current)
  }, [daysByKey, updateGapDirection])

  const handleDragStart = useCallback((e: DragStartEvent) => {
    setActiveDragTaskId(e.active.id as string)
    resetGapDirection()
  }, [resetGapDirection])

  const handleDragCancel = useCallback(() => {
    setActiveDragTaskId(null)
    resetGapDirection()
  }, [resetGapDirection])

  const handleDragEnd = useCallback(async (e: DragEndEvent) => {
    setActiveDragTaskId(null)
    const wasGapAfter = gapAfterRef.current
    resetGapDirection()
    const taskId = e.active.id as string
    const overId = e.over?.id as string | undefined
    if (!overId) return

    const original = tasksById.get(taskId)
    if (!original) return

    /*
      Zwei Arten von Zielen: eine **Zeile** (die Aufgaben-Kennung) und ein
      **Tag** (`YYYY-MM-DD`, für Tage ohne Zeilen). Auf eine Zeile abgelegt
      heißt „an diese Stelle" — davor oder dahinter, wie die Lücke stand; auf
      einen Tag „ans Ende dieses Tages".
    */
    const overTask = tasksById.get(overId) ?? null
    const overTaskId = overTask ? overId : null

    const targetDateKey = overTask
      ? dateKey(startOfDay(overTask.startDate ?? overTask.dueDate ?? new Date()))
      : overId

    const [y, mo, d] = targetDateKey.split('-').map(Number)
    if (Number.isNaN(y) || Number.isNaN(mo) || Number.isNaN(d)) return
    const newDate = new Date(y, mo - 1, d)

    const currentEffective = original.startDate ?? original.dueDate
    const currentKey = currentEffective ? dateKey(startOfDay(currentEffective)) : null
    const sameDay = currentKey === targetDateKey

    // Auf den Tag abgelegt, auf dem die Aufgabe ohnehin steht: nichts zu tun.
    // (Auf eine Zeile abgelegt schon — das ist ein Umordnen.)
    if (sameDay && !overTask) return

    // Abgelegt wird, wo die Lücke stand — dieselbe Entscheidung, die die
    // Strategie beim Ziehen getroffen hat, nicht eine zweite daneben.
    const dayTasks = daysByKey.get(targetDateKey)?.tasks ?? []
    const index = dropIndexInDay(taskId, dayTasks, overTaskId, wasGapAfter ?? true)

    const plan = planDayReorder(taskId, dayTasks, index)
    const movedPlan = plan.find(p => p.id === taskId)

    // Update whichever date field the task already uses. `startDate` wins
    // (both are present → prefer startDate, matching effectiveDate()).
    const dateUpdate = sameDay
      ? {}
      : original.startDate
        ? { startDate: newDate }
        : { dueDate: newDate }
    const updates = { ...dateUpdate, dayOrder: movedPlan?.dayOrder ?? null }

    // Optimistic move FIRST so the task visibly jumps to its new place in the
    // next paint. The hook auto-clears each override once Dexie catches up.
    setOptimisticTaskOverride(taskId, updates)
    for (const row of plan) {
      if (row.id === taskId) continue
      setOptimisticTaskOverride(row.id, { dayOrder: row.dayOrder })
    }

    await dataSource.updateTask(taskId, updates)
    const others = plan.filter(row => row.id !== taskId)
    if (others.length > 0) {
      await dataSource.updateTasks(others.map(row => ({ id: row.id, changes: { dayOrder: row.dayOrder } })))
    }
  }, [tasksById, daysByKey, setOptimisticTaskOverride, dataSource, gapAfterRef, resetGapDirection])

  // ── Duplicate task (Cmd/Ctrl+D). Clone all fields from the original,
  // but reset running-state (status/completed/actualDuration) and drop
  // any paused state so the duplicate shows up in Upcoming next to its
  // source — not in Ruhend.
  const handleDuplicateTask = useCallback(async (taskId: string) => {
    const original = await dataSource.getTask(taskId)
    if (!original) return
    const duplicate = await duplicateTask(dataSource, original, {
      // Im Projekt ans Ende ihres Abschnitts, nicht hinter die größte Nummer (Diff-Audit #276).
      order: await orderAtEndOfOwnPlace(dataSource, original),
      status: 'todo',
      completedAt: null,
      actualDuration: null,
      pausedAt: null,
    })
    selectTask(duplicate.id)
  }, [dataSource, selectTask])

  /* ⌘D über die ganze Auswahl; danach stehen die Kopien ausgewählt da. */
  const duplicateSelection = useCallback(async (ids: ReadonlySet<string>) => {
    const copies: string[] = []
    for (const id of ids) {
      const original = await dataSource.getTask(id)
      if (!original) continue
      const duplicate = await duplicateTask(dataSource, original, {
        order: await orderAtEndOfOwnPlace(dataSource, original),
        status: 'todo',
        completedAt: null,
        actualDuration: null,
        pausedAt: null,
      })
      copies.push(duplicate.id)
    }
    if (copies.length > 0) selection.replaceWith(copies)
  }, [dataSource, selection])

  /* **Das Menü „Aufgaben“ in der Menüleiste** (#257): dieselben Griffe wie die Tasten. */
  // Der Anker ist wie bei den Tasten die Zeile unter dem Cursor (s. o.).
  const menuAnchor = useMemo(
    () => menuAnchorOf(focusedId ? tasksById.get(focusedId) : null, [...tasksById.values()]),
    [focusedId, tasksById],
  )
  useAppTaskMenu({
    enabled: isActive,
    anchor: menuAnchor,
    multiSelected: selectedIds.size > 1,
    detailsOpen: details.open,
    canMove: true,
    onMenuShortcut: (taskId, target) => setMenuShortcut({ taskId, target }),
    onComplete: () => toggleCompleteMany(selectedIds),
    onFocus: handlePlayTask,
    onRest: handleRestShortcut,
    onFavorite: taskId => { void favoriteShortcut(taskId) },
    onToggleDetails: details.toggle,
    onShowInProject: showTaskInProject,
    onDuplicate: () => { void duplicateSelection(selectedIds) },
    onDelete: deleteSelection,
  })

  // ── Global keyboard shortcuts (Cmd/Ctrl+T, Cmd/Ctrl+Arrow) ──
  useEffect(() => {
    if (!isActive) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (editingTaskId) return
      const target = e.target as HTMLElement | null
      const tag = target?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return
      if (document.querySelector('[role="menu"]')) return
      const modifier = e.metaKey || e.ctrlKey
      if (!modifier) return

      // Nicht ⇧⌘T — das sind die Tags (#257).
      if (e.key.toLowerCase() === 't' && !e.shiftKey) {
        e.preventDefault()
        handleJumpToday()
        return
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        // Tab-switching uses Cmd+Ctrl+Arrow (and similar combos with
        // Shift/Alt). Only act on a clean Cmd-or-Ctrl + Arrow without
        // any extra modifier so we don't steal those shortcuts.
        if (e.shiftKey || e.altKey || (e.metaKey && e.ctrlKey)) return
        e.preventDefault()
        const delta = e.key === 'ArrowLeft' ? -1 : 1
        handleChipSelect(addMonths(selectedMonth, delta))
        return
      }
      // Cmd/Ctrl+D → duplicate the selected tasks (matches ProjectPage shortcut).
      // Nicht ⇧⌘D — das ist die Dauer (#257).
      if (e.key.toLowerCase() === 'd' && !e.shiftKey && selectedIds.size > 0) {
        e.preventDefault()
        duplicateSelection(selectedIds)
        return
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isActive, editingTaskId, handleJumpToday, handleChipSelect, selectedMonth, selectedIds, duplicateSelection])

  /*
    Das Nebeneinander von Liste und Karte samt seiner Bewegung kommt aus
    `ProjectPage.scss` — diese Seite trägt dafür dessen Klassen. Ihren eigenen
    Scroller hat sie schon, sie muss also nichts umbauen.
  */
  return (
    <TitleRecognitionScope lifeSpace={lifeSpace}>
    <TaskDetailsOpenerProvider value={details.opener}>
    <MoveMenuContext.Provider value={moveMenu}>
    <div className={`task-view upcoming-page project-page${details.open ? ' project-page--details-open' : ''}${details.visible && !details.open ? ' project-page--details-closing' : ''}`}>
    <div className="project-page__body upcoming-page__body">
    <div className="project-page__list-column">
      <div className="upcoming-page__header">
        <div className="upcoming-page__header-actions">
          {/* Ohne eine einzige Aufgabe gibt es nichts zu zeigen — der Knopf wäre
              ohne Wirkung (25.09.2026, wie im leeren Projekt). Offen bleibt er
              stehen, damit man wieder herauskommt. */}
          {(flatTasks.length > 0 || details.open) && (
            <DetailsToggle open={details.open} onToggle={details.toggle} />
          )}
        </div>
        <div className="page-help__row">
          <h1 className="upcoming-page__title">
            <UpcomingOpenIcon size={18} className="upcoming-page__title-icon" />
            {t('upcoming.title')}
          </h1>
          <PageHelpButton page="upcoming" />
        </div>
        <p className="upcoming-page__subtitle">{t('upcoming.subtitle')}</p>
      </div>

      <div className="upcoming-page__month-row">
        <MonthTitle month={visibleMonth} onYearSelect={handleYearSelect} />
        <MonthSelector
          selectedMonth={selectedMonth}
          todayMonth={todayMonth}
          taskCountsByMonth={taskCountsByMonth}
          onSelect={handleChipSelect}
          onJumpToday={handleJumpToday}
        />
      </div>

      <div className="upcoming-page__scroll-wrap" onPointerDown={handlePagePointerDown}>
        <DndContext
          sensors={dndSensors}
          collisionDetection={dropTarget}
          onDragStart={handleDragStart}
          onDragMove={handleDragMove}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <div
            className={`upcoming-page__scroll${scrollbarVisible ? ' upcoming-page__scroll--scroll-visible' : ''}`}
            ref={scrollRef}
            onScroll={handleScrollbar}
          >
            {/*
              EIN `SortableContext` über ALLE Monate und Tage — nicht einer je
              Tag. Nur so weiß dnd-kit beim Wechsel des Tages, wo die gezogene
              Zeile herkommt und wo sie hinsoll; mit einem Kontext je Tag wäre
              jeder Zug über die Grenze ein Zug ins Nichts. Welche Zeilen
              tatsächlich ausweichen, grenzt `dayAwareStrategy` ein.
            */}
            <SortableContext items={flatTaskIds} strategy={dayAwareStrategy}>
            <div className="upcoming-page__scroll-inner" ref={scrollInnerRef}>
              {/* Die Zeilenabfragen der geladenen Monate in drei Abos statt drei je Zeile (#303). */}
              <TaskRowDataScope taskIds={loadedTaskIds}>
              <LazyRowScope>
              {/* Eine Auswahl-Hülle über alle Tage: Die Markierung fährt auch von Tag zu Tag (#293). */}
              <SelectionHullScope>
              {months.map(bucket => {
                // Narrow edit/select state per month so months that don't
                // contain the target task hit MonthBlock's React.memo. Without
                // this, every `editingTaskId` flip re-renders all 7+ months
                // of lazy-loaded content (visibly stutters on arrow-key
                // navigation). O(1) via the precomputed index maps.
                const monthTasks = taskIdsByMonth.get(bucket.monthKey)
                const monthDays = dayKeysByMonth.get(bucket.monthKey)
                const monthHasEditing = editingTaskId !== null && monthTasks?.has(editingTaskId) === true
                const monthHasCreating = creatingDateKey !== null && monthDays?.has(creatingDateKey) === true
                return (
                  <MonthBlock
                    key={bucket.monthKey}
                    bucket={bucket}
                    registerAnchor={registerAnchor}
                    todayDateKey={todayDateKey}
                    editingTaskId={monthHasEditing ? editingTaskId : null}
                    selectedIdsByDay={selectedIdsByDay}
                    creatingDateKey={monthHasCreating ? creatingDateKey : null}
                    onStartEdit={startEdit}
                    onCancelEdit={cancelEdit}
                    onNavigateEdit={handleNavigateEdit}
                    onSelectTask={selection.handleRowClick}
                    onStartCreate={handleStartCreate}
                    onCancelCreate={handleCancelCreate}
                    onSubmitCreate={handleSubmitCreate}
                    onToggleComplete={wrappedToggleComplete}
                    onPlay={handlePlayTask}
                    onDelete={wrappedDelete}
                    onUpdateTask={handleUpdateTask}
                    onDuplicate={handleDuplicateTask}
                    onShowInProject={showTaskInProject}
                    onContextMenuBadgeUpdate={handleContextMenuBadgeUpdate}
                    getContextMenuMarks={getContextMenuMarks}
                    menuShortcut={menuShortcut}
                    onMenuShortcutClose={closeMenuShortcut}
                    isDragActive={activeDragTaskId !== null}
                  />
                )
              })}
              </SelectionHullScope>
              </LazyRowScope>
              </TaskRowDataScope>
            </div>
            </SortableContext>
          </div>
          <DragOverlay dropAnimation={null}>
            {activeDragTask ? (
              <TaskEntry mode="collapsed" task={activeDragTask} startDateLabelMode="relative" />
            ) : null}
          </DragOverlay>
        </DndContext>
        <div className="upcoming-page__fade-top" aria-hidden="true" />
        <div className="upcoming-page__fade-bottom" aria-hidden="true" />
      </div>
    </div>
      {details.visible && (
        <TaskDetails
          tasks={details.tasks}
          openSubtask={details.subtaskRequest}
          openComments={details.commentsRequest}
          openReminders={details.remindersRequest}
          focusedSeconds={details.focusedSeconds}
          onUpdate={handleDetailsUpdate}
          onClose={details.close}
          onStepTask={details.stepTask}
          onRest={details.onRest}
          canStepPrev={details.canStepPrev}
          canStepNext={details.canStepNext}
          onReleaseFocus={details.releaseFocusToList}
          focusTitleSignal={details.focusTitleSignal}
        />
      )}
    </div>
    </div>
    </MoveMenuContext.Provider>
    </TaskDetailsOpenerProvider>
    </TitleRecognitionScope>
  )
}